import { useEffect, useMemo, useState } from 'react'

import { useResourceRecordsContext } from '@/shared/hooks'
import { LoadWrapper } from '@/shared/ui'
import { Resource } from '@/shared/ui/Resource'
import {
  PageHeader,
  ResourceActiveFilterChips,
  ResourceFilterBar,
  Screen
} from '@/shared/ui/Layout'
import { PageSearchFilterRowSkeleton } from '@/shared/ui/Skeleton'

import { DigitalProductPartnersCard } from '../Card'
import { deriveCategoryOptions } from '../lib/categoryOptions'
import { shouldShowPartnersListInitialSkeleton } from '../lib/shouldShowPartnersListInitialSkeleton'
import { usePartnersListFilters } from '../hooks/usePartnersListFilters'
import { usePartnersTopLikes } from '../hooks/usePartnersTopLikes'
import { PartnersAdvancedFilter } from './PartnersAdvancedFilter'
import { PartnersListSkeleton } from './PartnersListSkeleton'
import { PartnersTopLikesCarousel } from './PartnersTopLikesCarousel'

type PartnersListScreenProps = {
  title: string
  showTopLikes?: boolean
}

export const PartnersListScreen = ({
  title,
  showTopLikes = true
}: PartnersListScreenProps) => {
  const {
    records: partners,
    isLoading: isPartnersLoading,
    isSuccess: isPartnersSuccess,
    loadWrapperConfig,
    config: { apiBasePath }
  } = useResourceRecordsContext() as {
    records?: Record<string, unknown>[]
    isLoading?: boolean
    isSuccess?: boolean
    loadWrapperConfig: Record<string, unknown>
    config: { apiBasePath: string }
  }

  const partnerList = Array.isArray(partners) ? partners : []
  const topLikes = usePartnersTopLikes(apiBasePath, showTopLikes)
  const topLikeIdSet = useMemo(() => new Set(topLikes), [topLikes])

  const remainingPartners = showTopLikes
    ? partnerList.filter(
        partner => !topLikeIdSet.has(partner.id as number)
      )
    : partnerList

  const [categoryOptions, setCategoryOptions] = useState<
    ReturnType<typeof deriveCategoryOptions>
  >([])

  useEffect(() => {
    setCategoryOptions(prev => {
      const next = deriveCategoryOptions(partnerList, prev)

      return next.length === prev.length ? prev : next
    })
  }, [partnerList])

  const filters = usePartnersListFilters(categoryOptions)

  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false)

  useEffect(() => {
    if (isPartnersSuccess) setHasInitiallyLoaded(true)
  }, [isPartnersSuccess])

  const showInitialSkeleton = shouldShowPartnersListInitialSkeleton({
    hasInitiallyLoaded,
    isLoading: Boolean(isPartnersLoading),
    isSuccess: Boolean(isPartnersSuccess)
  })

  return (
    <Screen>
      <Screen.Context>
        <PageHeader title={title} />
      </Screen.Context>

      <Screen.Sticky className="flex flex-col gap-2">
        {showInitialSkeleton ? (
          <PageSearchFilterRowSkeleton />
        ) : (
          <>
            <ResourceFilterBar
              searchValue={filters.searchInput}
              onSearchChange={filters.setSearchInput}
              onSearchSubmit={filters.flushSearch}
              searchPlaceholder="Buscar parceiro..."
              searchAriaLabel="Buscar parceiro"
              advancedFilter={
                <PartnersAdvancedFilter
                  filters={filters.advancedFilters}
                  emptyDraft={filters.emptyAdvancedDraft}
                  options={filters.filterOptions}
                  hasActiveFilters={filters.hasActiveAdvanced}
                  onApplyDraft={filters.applyAdvancedDraft}
                  onReset={filters.resetAdvanced}
                  className="shrink-0"
                />
              }
            />

            <ResourceActiveFilterChips
              chips={filters.activeChips}
              onRemove={id => {
                if (id === 'category') filters.resetAdvanced()
              }}
            />
          </>
        )}
      </Screen.Sticky>

      <Screen.Content className="flex flex-col gap-3 pb-6 pt-2">
        {showInitialSkeleton ? (
          <PartnersListSkeleton />
        ) : (
          <>
            {showTopLikes && (
              <PartnersTopLikesCarousel
                partners={partnerList}
                topLikeIds={topLikes}
              />
            )}

            <LoadWrapper
              {...loadWrapperConfig}
              skeleton={<PartnersListSkeleton />}
            >
              <div className="flex flex-col gap-3">
                {remainingPartners.map(partner => (
                  <Resource.Root
                    key={partner.id as number}
                    type="Partner"
                    id={partner.id as number}
                    pointsReward={partner.point_reward as number | undefined}
                    coinsReward={partner.coin_reward as number | undefined}
                  >
                    <DigitalProductPartnersCard {...partner} />
                  </Resource.Root>
                ))}
              </div>
            </LoadWrapper>
          </>
        )}
      </Screen.Content>
    </Screen>
  )
}
